import { Link } from 'react-router-dom';
import { LayoutGrid, Minimize2 } from 'lucide-react';
import { useChrome } from '../context/ChromeContext';
import { WORKTOP_PATH } from '@/lib/arche-url';
import { useGlobalFiasAIOptional } from '@/components/fias-ai/hooks/GlobalFiasAIContext';

interface ExpandedArcheHeaderProps {
  /** Canonical arche id (`arche_*`) whose focus-mode chrome this bar restores. */
  archeId: string;
}

/**
 * The thin top bar shown while an arche is EXPANDED (focus mode — the platform
 * Header, the suggested-arches sidebar, and the arche's own ArcheHeader are all
 * hidden so the arche fills the viewport). Once chrome is hidden it is the ONLY
 * in-app way back, so every focus-mode-capable arche must surface it:
 *
 * - Normal-mode arches get it for free — `ArcheLayout` renders it centrally.
 * - An arche with its OWN layout (e.g. email) passes `hideExpandedHeader` to
 *   `ArcheLayout` and renders its own restore affordance (`FocusModeRestoreControls`)
 *   in a spot that fits its chrome instead.
 *
 * Four controls: leave to Fias home, jump to the Worktop, open Fias AI, and
 * collapse back to the standard (chrome-shown) view. Home and Worktop are
 * separate destinations — `/` is the marketing/landing home, the Worktop is the
 * user's own arche board — and in focus mode the platform Header that normally
 * carries the Worktop link is hidden, so this bar is the only route to it.
 * Self-guards on this arche's chrome-hidden state,
 * so it is inert (renders nothing) when the arche is not expanded — including in
 * arche tests that render it without a ChromeProvider (the context's safe
 * default reports "not hidden").
 */
export function ExpandedArcheHeader({ archeId }: ExpandedArcheHeaderProps) {
  const { isHeadersHidden, showHeaders } = useChrome();
  const ai = useGlobalFiasAIOptional();
  if (!isHeadersHidden(archeId)) return null;

  return (
    <div className="flex-shrink-0 border-b border-border bg-card/80 backdrop-blur-sm">
      <div className="flex items-center justify-between gap-2 px-3 py-1.5">
        <div className="flex items-center gap-1">
          <Link
            to="/"
            className="flex items-center gap-2 rounded-md px-1.5 py-1 text-sm text-muted-foreground transition-colors hover:text-primary"
            title="Back to Fias"
            aria-label="Back to Fias"
          >
            <img src="/fias-logo.png" alt="" className="h-5 w-auto dark:invert" />
          </Link>
          <Link
            to={WORKTOP_PATH}
            className="flex items-center gap-1.5 rounded-md px-2 py-1 text-sm text-muted-foreground transition-colors hover:text-primary"
            title="Go to Worktop"
            aria-label="Go to Worktop"
          >
            <LayoutGrid className="h-4 w-4" />
            <span className="hidden sm:inline">Worktop</span>
          </Link>
        </div>

        <div className="flex items-center gap-1">
          {/* Optional: only when the global assistant provider is mounted (it is
              app-wide, but arche unit tests render this bar without it). */}
          {ai && (
            <button
              type="button"
              onClick={() => ai.openPanel()}
              className="flex items-center gap-1.5 rounded-md px-2 py-1 text-sm text-muted-foreground transition-colors hover:text-primary"
              title="Open Fias AI"
              aria-label="Open Fias AI"
            >
              <img src="/Heptagen_Logo_1.png" alt="" className="h-5 w-5" />
              <span className="hidden sm:inline">Fias AI</span>
            </button>
          )}
          <button
            type="button"
            onClick={() => showHeaders(archeId)}
            className="flex items-center gap-1.5 rounded-md px-2 py-1 text-sm text-muted-foreground transition-colors hover:text-primary"
            title="Collapse to standard view"
            aria-label="Collapse to standard view"
          >
            <Minimize2 className="h-4 w-4" />
            <span className="hidden sm:inline">Collapse</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default ExpandedArcheHeader;
